import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { TranslocoService } from '@jsverse/transloco';

import { QueryParams } from 'app/providers/api.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { ActionPlan, ActionPlanService } from 'app/providers/storage-api/actionplan.service';
import { AlertService } from 'app/services/alert.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { ActionPlanUtils } from 'app/utils/action-plan.utils';
import { PaginationEvent } from '../paginator/paginator.component';

interface ColumnDefinition {
  name: string;
  defName: string;
  sortName?: string;
}

interface ActionPlanDataItem extends ActionPlan {
  isExpired: boolean;
  expiredTooltip: string;
  isSelected: boolean;
}

export interface SelectionEvent {
  id: string;
  isChecked: boolean;
}

type TableMode = 'select' | 'view';

@EnableClassTelemetry()
@Component({
  selector: 'app-action-plan-table',
  templateUrl: './action-plan-table.component.html',
  styleUrls: ['./action-plan-table.component.scss'],
  standalone: false,
})
export class ActionPlanTableComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() mode: TableMode;
  @Input() selectedIds?: string[];

  @Output() details = new EventEmitter<ActionPlan>();
  @Output() edit = new EventEmitter<ActionPlan>();
  @Output() select = new EventEmitter<SelectionEvent>();

  isLoading = false;
  groupId: string;
  isReadOnly: boolean;

  private readonly _columns: ColumnDefinition[] = [
    {
      name: 'project',
      defName: 'project_def',
      sortName: 'metadata:projectName',
    },
    {
      name: 'breakdown',
      defName: 'breakdown_def',
      sortName: 'metadata:breakdown',
    },
    {
      name: 'driver',
      defName: 'driver_def',
      sortName: 'metric_name',
    },
    {
      name: 'status',
      defName: 'status_def',
      sortName: 'status',
    },
    {
      name: 'completed',
      defName: 'action_items',
      sortName: 'completed_child_count',
    },
    {
      name: 'past_due',
      defName: 'action_items',
      sortName: 'past_due_child_count',
    },
    {
      name: 'due_date',
      defName: 'due_date_def',
      sortName: 'due_date',
    },
  ];
  private readonly _selectColumn: ColumnDefinition = {
    name: 'select',
    defName: 'select_def',
  };
  private readonly _actionsColumn: ColumnDefinition = {
    name: 'actions',
    defName: 'actions_def',
  };
  displayedColumns: string[];
  defColumns: string[];
  dataSource: ActionPlanDataItem[] = [];
  actionPlanMenuItem: ActionPlan;
  sort: Sort = {
    active: 'breakdown_def',
    direction: 'asc',
  };

  length = 0;
  pageIndex = 0;
  pageSize = 25;
  pageSizeOptions: number[] = [5, 10, 25, 50];

  constructor(
    groupService: GroupService,
    authService: AuthService,
    private actionPlanService: ActionPlanService,
    private alertService: AlertService,
    private translocoService: TranslocoService
  ) {
    this.groupId = groupService.getSelectedGroupId();
    this.isReadOnly = authService.isReadOnly;
  }

  async ngOnInit(): Promise<void> {
    this.setColumns();

    await this.listActionPlans();
  }

  onSortChanged(sort: Sort): void {
    this.sort = sort;

    this.listActionPlans();
  }

  onPageChanged(event: PaginationEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    this.listActionPlans();
  }

  onActionsMenuToggle(actionPlan: ActionPlan): void {
    this.actionPlanMenuItem = actionPlan;
  }

  onDetailsOnCell(actionPlan: ActionPlan): void {
    this.details.emit(actionPlan);
  }

  onDetails(): void {
    this.details.emit(this.actionPlanMenuItem);
  }

  onEdit(): void {
    this.edit.emit(this.actionPlanMenuItem);
  }

  onSelect(id: string, isChecked: boolean): void {
    this.select.emit({ id, isChecked });
  }

  async onDelete(): Promise<void> {
    try {
      await this.actionPlanService.deleteActionPlan(this.groupId, this.actionPlanMenuItem.plan_id);

      // go to previous page
      if (this.dataSource.length === 1 && this.pageIndex > 0) {
        this.pageIndex--;
      }

      this.listActionPlans();

      this.alertService.showToast(
        this.translocoService.translate('action-plan-table.delete-success')
      );
    } catch (error) {
      this.alertService.showErrorToast(
        this.translocoService.translate('action-plan-table.delete-failure'),
        error
      );
    }
  }

  private async listActionPlans(): Promise<void> {
    this.isLoading = true;

    try {
      const params: QueryParams = {
        page_size: this.pageSize,
        page_number: this.pageIndex,
        sort: this.getSortingField(),
        reverse_order: this.sort.direction === 'desc',
      };
      const actionPlanListResult = await this.actionPlanService.listActionPlans(
        this.groupId,
        params
      );

      this.dataSource = actionPlanListResult.data.map((a) => ({
        ...a,
        isExpired: ActionPlanUtils.isExpired(a),
        expiredTooltip: ActionPlanUtils.getExpiredLabel(a),
        isSelected: this.selectedIds?.includes(a.plan_id),
      }));
      this.length = actionPlanListResult.total_elements;
    } catch (error) {
      this.alertService.showErrorToast(
        this.translocoService.translate('action-plan-table.load-failure'),
        error
      );
    }

    this.isLoading = false;
  }

  private setColumns(): void {
    const columns: ColumnDefinition[] = [];

    switch (this.mode) {
      case 'select':
        columns.push(this._selectColumn, ...this._columns);
        break;
      case 'view':
        columns.push(...this._columns, this._actionsColumn);
        break;
    }

    this.displayedColumns = columns.map((c) => c.name);
    this.defColumns = [...new Set(columns.map((c) => c.defName))];
  }

  private getSortingField(): string {
    const columnByDefName = this._columns.filter((c) => c.defName === this.sort.active);
    if (columnByDefName.length > 0) {
      return columnByDefName[0].sortName;
    }

    return this._columns.filter((c) => c.name === this.sort.active)[0].sortName;
  }
}
